import type { TimeSlot } from "@/lib/actions/actions";
import { useIsFetching } from "@tanstack/react-query";
import { format } from "date-fns";
import { filterBookableSlots } from "../bookable-slots";
import { ProviderCard } from "./ProviderCard";
interface Provider {
  label: string;
  title?: string;
  value: string;
  img: string | null;
}

interface ProviderListProps {
  providers: Provider[];
  formattedSlotsByProvider: Record<string, TimeSlot[]>;
  currentDisplayDates: Date[];
  onTimeSlotSelect: (timeSlot: TimeSlot) => Promise<void>;
  timeZone: string;
  earliestBookableTime: Date;
}

export function ProviderList({
  providers,
  formattedSlotsByProvider,
  currentDisplayDates,
  onTimeSlotSelect,
  timeZone,
  earliestBookableTime,
}: ProviderListProps) {
  const isFetchingTimeSlots = !!useIsFetching({
    queryKey: ["fetchTimeSlots"],
  });

  return (
    <div className="w-full flex flex-col gap-4">
      {providers.map((provider) => {
        const providerId = Number.parseInt(provider.value);
        const providerSlots = formattedSlotsByProvider[providerId] || [];
        const uniqueProviderSlots = providerSlots.filter(
          (slot, index, self) =>
            index === self.findIndex((t) => t.dateTime === slot.dateTime),
        );

        // Filter slots for current display dates
        const availableSlots = uniqueProviderSlots.filter((slot) => {
          const slotDate = new Date(slot.dateTime);
          return currentDisplayDates.some(
            (date) =>
              format(date, "yyyy-MM-dd") === format(slotDate, "yyyy-MM-dd"),
          );
        });

        const filteredSlots = filterBookableSlots(
          availableSlots,
          earliestBookableTime,
        );

        return (
          <ProviderCard
            key={provider.value}
            provider={provider}
            availableSlots={filteredSlots}
            currentDisplayDates={currentDisplayDates}
            onTimeSlotSelect={onTimeSlotSelect}
            isFetchingTimeSlots={isFetchingTimeSlots}
            timeZone={timeZone}
          />
        );
      })}
    </div>
  );
}
